import React, { ReactNode, useMemo } from 'react'
import { useRequest } from 'ahooks'
import { Form, message } from 'antd';
import DrawerForm from 'components/common/form/DrawerForm';
import { getRet00cdTree } from 'components/common/services/ret00cds'
import { createJsonValidator, formatRet00cExampleFields } from './jsonExampleUtils'
import JsonCodeTextarea from './JsonCodeTextarea'

interface Ret00cFormProps {
  record?: any 
  buttonType?: "primary" | "default" | "dashed" | "link" | "text",
  buttonText?: string | ReactNode,
  submitSuccess?: (e: any) => void
}

interface BuildRet00cFormOptions {
  compactForCreate?: boolean
  compactMode?: boolean
  includeExamples?: boolean
}

export const buildRet00cFormData = (
  _record?: any,
  groupOptions: any[] = [],
  options: BuildRet00cFormOptions = {}
) => {
  const includeExamples = options.includeExamples !== false

  return [
    {
      key: 'id',
      label: 'ID',
      type: 'input',
      hidden: true,
    },
    {
      key: 'http_method',
      label: 'HTTP',
      type: 'select',
      rules: [{ required: true, message: '请选择请求方式' }],
      formItemClassName: 'col-span-1 md:col-span-1',
      options: [
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' },
      ],
    },
    {
      key: 'name',
      label: '接口名称',
      type: 'input',
      rules: [{ required: true, message: '请输入接口名称' }],
      placeholder: '名称',
      formItemClassName: 'col-span-1 md:col-span-3',
    },
    {
      key: 'path',
      label: 'API Path',
      type: 'input',
      rules: [{ required: true, message: '请输入接口路径' }],
      placeholder: 'API Path',
      formItemClassName: 'col-span-1 md:col-span-4',
    },
    {
      key: 'ret00cd_id',
      label: '接口分组',
      type: 'tree-select',
      rules: [{ required: true, message: '请选择接口分组' }],
      options: groupOptions,
      placeholder: '请选择接口分组',
      formItemClassName: 'col-span-1 md:col-span-3',
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      rules: [{ required: true, message: '请选择状态' }],
      formItemClassName: 'col-span-1 md:col-span-1',
      options: [
        { label: '草稿', value: 'draft' },
        { label: '启用', value: 'enabled' },
        { label: '停用', value: 'disabled' },
      ],
    },
    ...(includeExamples ? [
      {
        key: 'request_example',
        label: '请求示例',
        type: 'textarea',
        rules: [{ validator: createJsonValidator('请求示例') }],
        validateTrigger: ['onChange', 'onBlur'],
        placeholder: '请输入合法 JSON',
        rows: 8,
        renderFormItem: (itemProps: any) => <JsonCodeTextarea rows={itemProps.rows} placeholder={itemProps.placeholder} />,
        formItemClassName: 'col-span-1 md:col-span-6',
      },
      {
        key: 'response_example',
        label: '响应示例',
        type: 'textarea',
        rules: [{ validator: createJsonValidator('响应示例') }],
        validateTrigger: ['onChange', 'onBlur'],
        placeholder: '请输入合法 JSON',
        rows: 8,
        renderFormItem: (itemProps: any) => <JsonCodeTextarea rows={itemProps.rows} placeholder={itemProps.placeholder} />,
        formItemClassName: 'col-span-1 md:col-span-6',
      },
    ] : []),
  ]
}

function Ret00cForm({ record, buttonText, buttonType, submitSuccess }: Ret00cFormProps): JSX.Element {
  const [form] = Form.useForm();
  const submitPath = record ? `/console/ret00cs/${record.id}` : '/console/ret00cs'
  const submitMethod = record ? 'PUT' : 'POST'
  const { data } = useRequest(getRet00cdTree)
  const groupOptions = useMemo(() => (data as any)?.data?.data?.items || [], [data])
  const initialRecord = useMemo(() => (
    record || {
      http_method: 'POST',
      status: 'enabled',
    }
  ), [record])
  const formattedRecord = useMemo(() => formatRet00cExampleFields(initialRecord), [initialRecord])
  const formData = useMemo(() => buildRet00cFormData(formattedRecord, groupOptions), [formattedRecord, groupOptions])
  
  return (
    <DrawerForm
      title={record ? '编辑接口' : '添加接口'}
      submitPath={submitPath}
      submitMethod={submitMethod}
      buttonType={buttonType}
      buttonText={buttonText}
      submitSuccess={(e) => {
        message.success('操作成功')
        submitSuccess?.(e)
      }}
      form={form}
      wrapperClassNames='grid grid-cols-1 md:grid-cols-12 gap-x-4'
      drawerProps={{
        width: 720,
      }}
      data={formattedRecord}
      formData={formData}
    />
  )
}

export default Ret00cForm
